import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, featuredMatches } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { getWebPageSchema } from '@/components/base/SEOHead';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const displayCategory = category || categories[0];

  const filteredMatches = featuredMatches.filter((m) => m.category === (slug || 'football'));
  const filteredArticles = articles.filter((a) => a.category === (slug || 'football'));
  const allCategories = categories;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });

    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.textContent = JSON.stringify(
      getWebPageSchema(
        `${displayCategory.name} - 华体会官网`,
        displayCategory.description,
        `https://www.huatiyuiguanwang.com/category/${slug || 'football'}`
      ),
      null,
      2
    );
    document.head.appendChild(ld);

    return () => {
      document.querySelectorAll('script[type="application/ld+json"]').forEach((el) => {
        if (el.textContent?.includes('WebPage')) el.remove();
      });
    };
  }, [slug]);

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: displayCategory.name },
          ]}
        />

        <section className="w-full py-8 md:py-12 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 flex items-center justify-center bg-accent-100 rounded-xl">
                <i className={`${displayCategory.icon} text-accent-700 text-xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  {displayCategory.name}
                </h1>
                <p className="text-sm text-foreground-600 mt-1">{displayCategory.description}</p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-8">
              {allCategories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                    (slug || 'football') === cat.slug
                      ? 'bg-accent-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-accent-100 hover:text-accent-700 border border-background-200/60'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {filteredMatches.length > 0 && (
          <section className="w-full py-8 md:py-12 bg-background-100">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                热门赛事
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredMatches.map((match) => (
                  <div
                    key={match.id}
                    className="bg-background-50 rounded-lg p-5 border border-background-200/60"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-medium text-accent-700 bg-accent-100 px-2.5 py-1 rounded-full">
                        {match.league}
                      </span>
                      <span className="text-xs text-foreground-500">{match.time}</span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex flex-col items-center flex-1">
                        <div className="w-10 h-10 flex items-center justify-center bg-background-200 rounded-full mb-1.5">
                          <i className={`${match.homeIcon} text-foreground-700 text-lg`}></i>
                        </div>
                        <span className="text-sm font-semibold text-foreground-900">{match.home}</span>
                      </div>
                      <span className="text-xs text-foreground-500 font-medium">VS</span>
                      <div className="flex flex-col items-center flex-1">
                        <div className="w-10 h-10 flex items-center justify-center bg-background-200 rounded-full mb-1.5">
                          <i className={`${match.awayIcon} text-foreground-700 text-lg`}></i>
                        </div>
                        <span className="text-sm font-semibold text-foreground-900">{match.away}</span>
                      </div>
                    </div>
                    {'draw' in match.odds ? (
                      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-background-200/60">
                        <div className="flex-1 text-center">
                          <span className="block text-xs text-foreground-500">主胜</span>
                          <span className="text-sm font-bold text-accent-700">{match.odds.home}</span>
                        </div>
                        <div className="flex-1 text-center">
                          <span className="block text-xs text-foreground-500">平局</span>
                          <span className="text-sm font-bold text-foreground-700">{match.odds.draw}</span>
                        </div>
                        <div className="flex-1 text-center">
                          <span className="block text-xs text-foreground-500">客胜</span>
                          <span className="text-sm font-bold text-foreground-700">{match.odds.away}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-background-200/60">
                        <div className="flex-1 text-center">
                          <span className="block text-xs text-foreground-500">主胜</span>
                          <span className="text-sm font-bold text-accent-700">{match.odds.home}</span>
                        </div>
                        <div className="flex-1 text-center">
                          <span className="block text-xs text-foreground-500">客胜</span>
                          <span className="text-sm font-bold text-foreground-700">{match.odds.away}</span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {filteredArticles.length > 0 && (
          <section className="w-full py-8 md:py-12 bg-background-50">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                相关资讯
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group block bg-background-100 rounded-lg overflow-hidden border border-background-200/60 hover:border-accent-300 transition-all duration-300"
                  >
                    <div className="aspect-[16/9] overflow-hidden bg-background-200">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-foreground-500">{article.date}</span>
                        <span className="text-xs text-foreground-500">{article.readTime}</span>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-900 leading-snug group-hover:text-accent-700 transition-colors duration-200 line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-xs text-foreground-600 mt-2 leading-relaxed line-clamp-2">
                        {article.excerpt}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="w-full py-10 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-4">
              热门联赛
            </h3>
            <div className="flex flex-wrap gap-2">
              {displayCategory.popularLeagues.map((league) => (
                <span
                  key={league}
                  className="px-3 py-1.5 bg-background-50 border border-background-200/60 rounded-md text-xs text-foreground-700 font-medium"
                >
                  {league}
                </span>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}